import type { InvoiceStatus } from "./invoice";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TaxQuestionKey } from "../taxSavingRules";
import { RequiredAction } from "../requiredActions";
import { FIELD_UPDATE_SOURCE } from "../constants";
import { CounterpartyVerifiedScope } from "../constants/counterparty";
import { Entity, EntityBase } from "./entity";
import { FinancialAccount, FinancialAccountBase } from "./financialAccount";
import { TaxQuestionAnswer, TaxQuestionAnswerBase } from "./taxQuestionAnswer";
import { TransactionMatch, TransactionMatchBase } from "./transactionMatch";
import { Counterparty } from "./counterparty";
import { Category } from "./category";
import { Account } from "./domains/accounting/account";
import { Subaccount } from "./subaccount";
import type { TransactionSchedule } from "@common/schemas/transaction.schema";
import type { MatchedSchedule } from "@common/schemas/domains/accounting/scheduleMatchedDisplay.schema";

export enum TransactionType {
    EXTERNAL = "external",
    STRIPE = "stripe",
    IMPORTED = "imported",
    RAMP = "ramp",
    MERCURY = "mercury",
    PAYPAL = "paypal",
    MANUAL = "manual",
    CHECK = "check",
    DEPOSIT = "deposit",
    GUSTO = "gusto",
    BILLCOM = "billcom",
    ADP_RUN = "adp_run",
}

export type CashItemTransactionType =
    | TransactionType.CHECK
    | TransactionType.DEPOSIT;

export const CASH_ITEM_TRANSACTION_TYPE_LABELS: Record<
    CashItemTransactionType,
    string
> = {
    [TransactionType.CHECK]: "Check",
    [TransactionType.DEPOSIT]: "Deposit",
};

export const CASH_ITEM_TRANSACTION_TYPES: CashItemTransactionType[] = [
    TransactionType.CHECK,
    TransactionType.DEPOSIT,
];

export enum TransactionStatus {
    PENDING = "pending",
    COMPLETED = "completed",
    FAILED = "failed",
}

export enum TransactionClassification {
    BUSINESS = "business",
    PERSONAL = "personal",
}

export enum CategoryBackupKeys {
    USER = "user",
    OPS = "ops",
    CATEGORIZATION_MODEL = "categorizationModel",
    SIMILARITY = "similarity",
    CATEGORIZATION_RULES = "categorizationRules",
    TRANSFER_MATCHING = "transferMatching",
}

export type CategoryBackup = Partial<Record<CategoryBackupKeys, string>>;

/**
 * Scalar fields for Transaction (no relations).
 * Internal type - use TransactionBase or Transaction instead.
 */
interface TransactionScalars {
    id: number;
    amount: number;
    originalAmount: number;
    foreignCurrencyAmount: number | null;
    foreignCurrency: string | null;
    /** @deprecated Use `dateOnly` for the accounting calendar day. Legacy UTC timestamp; persist on writes only. */
    date: Date;
    dateOnly?: string | null;
    type: TransactionType;
    status: TransactionStatus;
    bankDescription: string;
    normalizedBankDescription: string | null;
    normalizedDescriptionId: string | null;
    displayDescription: string | null;
    categoryId: string;
    accountId: string | null;
    accrualAccountId: string | null;
    suggestedAccrualAccountId: string | null;
    entityId: number;
    financialAccountId: number;
    businessCategoryId: string | null;
    personalCategoryId: string | null;
    categoryBackup: CategoryBackup | null;
    counterpartyId: string | null;
    customerId: string | null;
    billableExpenseInvoiceId: string | null;
    memo: string | null;
    amountSaved: number | null;
    potentialAmountSaved: number | null;
    requiredActions: RequiredAction[];

    taxQuestions: TaxQuestionKey[];
    deductionRate: number;
    requirementsSnoozed: boolean;
    isIgnored: boolean;
    isBusiness: boolean;
    excludeFrom1099?: boolean;

    inReview: boolean;

    pairedTransferId?: number | null;
    splitParentId?: number | null;
    splitDescription?: string | null;
    subaccountId?: string | null;

    suggestedCategories?: string[] | null;
    reconciledInId?: string | null;

    categorizationSource: FIELD_UPDATE_SOURCE | null;
    counterpartyIdSource: FIELD_UPDATE_SOURCE | null;
    categoryIdLastUpdatedAt?: Date | null;
    counterpartyIdLastUpdatedAt?: Date | null;
    counterpartyVerifiedScope?: CounterpartyVerifiedScope | null;
    counterpartyPredicted?: boolean;
    similarityVectorGenerated?: boolean;
}

export interface ClassAssignment {
    classId: string;
    rootClassId: string;
}

/**
 * Minimal reference to the native invoice a billable expense transaction was
 * billed on (transaction.billableExpenseInvoiceId). Only carries the fields
 * needed to render/filter/sort by invoice status on the transactions list -
 * not the full Invoice type.
 */
export interface TransactionBillableExpenseInvoiceReference {
    id: string;
    status: InvoiceStatus;
}

/**
 * Per-ledger account override entry as it appears on API responses.
 * Each row pins a specific account id to a specific ledger; the legacy
 * `transaction.accountId` / `transaction.accrualAccountId` columns are
 * still hydrated from these rows on the entity's primary cash / accrual
 * ledger.
 */
export interface AccountOverrideEntry {
    accountingLedgerId: string;
    accountId: string;
}

/**
 * Base type for Transaction - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface TransactionBase extends TransactionScalars {
    financialAccount?: FinancialAccountBase;
    entity?: EntityBase;
    taxQuestionAnswers?: Array<TaxQuestionAnswerBase<TransactionBase>>;
    transactionMatches?: TransactionMatchBase[];
    pairedTransfer?: (TransactionBase & { entityId?: number }) | null;
    counterparty?: Counterparty | null;
    customer?: Counterparty | null;
    billableExpenseInvoice?: TransactionBillableExpenseInvoiceReference | null;
    splits?: TransactionBase[] | null;
    classAssignments?: ClassAssignment[];
    category?: Category;
    businessCategory?: Category | null;
    account?: Account | null;
    accrualAccount?: Account | null;
    suggestedAccrualAccount?: Account | null;
    subaccount?: Subaccount | null;
    clearedDateOnly?: string | null;
    journalEntries?: TransactionJournalEntry[];
    accountOverrides?: AccountOverrideEntry[];
    schedule?: TransactionSchedule;
    matchedSchedules?: MatchedSchedule[];
}

/**
 * Strict type for Transaction - requires loaded relations.
 * Used by DTOs and service boundaries after relations are loaded.
 */
export interface Transaction extends Omit<
    TransactionBase,
    | "financialAccount"
    | "entity"
    | "taxQuestionAnswers"
    | "transactionMatches"
    | "pairedTransfer"
    | "splits"
> {
    financialAccount: FinancialAccount;
    entity: Entity;
    taxQuestionAnswers: Array<TaxQuestionAnswer<Transaction>>;
    transactionMatches?: TransactionMatch[];
    pairedTransfer?: (Transaction & { entityId?: number }) | null;
    splits?: Transaction[] | null;
}

export type TransactionListItemBaseDto = Pick<
    TransactionScalars,
    | "id"
    | "date"
    | "dateOnly"
    | "amountSaved"
    | "amount"
    | "status"
    | "splitParentId"
    | "counterpartyId"
    | "customerId"
    | "billableExpenseInvoiceId"
    | "categoryId"
    | "isIgnored"
    | "isBusiness"
    | "excludeFrom1099"
    | "type"
    | "suggestedCategories"
    | "requiredActions"
    | "requirementsSnoozed"
    | "potentialAmountSaved"
    | "bankDescription"
    | "splitDescription"
    | "originalAmount"
    | "pairedTransferId"
    | "inReview"
    | "memo"
    | "displayDescription"
    | "deductionRate"
    | "businessCategoryId"
    | "entityId"
    | "accountId"
    | "accrualAccountId"
    | "suggestedAccrualAccountId"
    | "subaccountId"
    | "reconciledInId"
    | "categoryIdLastUpdatedAt"
> & { financialAccountId?: number; requiredActionsCount?: number | null };

export interface TransactionListItemDto extends TransactionListItemBaseDto {
    financialAccount: FinancialAccount;
    entity: Entity;

    category?: Category;
    counterparty?: Counterparty | null;
    customer?: Counterparty | null;
    billableExpenseInvoice?: TransactionBillableExpenseInvoiceReference | null;
    classAssignments?: ClassAssignment[];
    transactionMatches?: TransactionMatch[];
    taxQuestionAnswers: TaxQuestionAnswer[];
    journalEntries?: TransactionJournalEntry[];
    pairedTransfer?: PairedTransfer | null;
    subaccount?: Subaccount | null;
}

export interface TransactionJournalEntry {
    id: string;
    accountId: string;
    lineOrder: number;
    referenceNumber: string;
    accountingLedger: {
        id: string;
        entityId: number;
        basis: AccountingLedgerBasis;
    };
}

export interface PairedTransfer extends Pick<
    TransactionListItemBaseDto,
    | "id"
    | "displayDescription"
    | "date"
    | "dateOnly"
    | "amount"
    | "financialAccountId"
    | "categoryId"
    | "bankDescription"
> {
    entityId?: number;
}
